import React, { useState, useRef, useEffect } from "react";
// CSS
import "./customColorPicker.css";
// Components
import Button from "../Other Components/button";
import InputField from "../Other Components/inputField";

//Hooks
import { useTranslation } from "react-i18next";

export default function CustomColorPicker({
  initialColor,
  onSelectColor,
  onCancel,
}) {
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(0);
  const [lightness, setLightness] = useState(100);
  const [normLightness, setNormLightness] = useState(100);
  const [inputValue, setInputValue] = useState("");
  const [color, setColor] = useState(initialColor);
  const colorWindowRef = useRef(null);
  const popupRef = useRef(null);

  const { t } = useTranslation();

  useEffect(() => {
    setColor(initialColor);
    const valuesString = initialColor.substring(4, initialColor.length - 1);
    const values = valuesString.split(",");
    const hue = parseInt(values[0].trim(), 10);
    const saturation = parseInt(values[1].trim().replace("%", ""), 10);
    const lightness = parseInt(values[2].trim().replace("%", ""), 10);

    setHue(hue);
    setSaturation(saturation.toFixed(2));
    setLightness(lightness.toFixed(2));
    setInputValue(`${hue}, ${saturation}, ${lightness}`);
  }, [initialColor]);

  useEffect(() => {
    setColor(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }, [hue, saturation, lightness]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onCancel();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onCancel]);

  const handleHueChange = (e) => {
    setHue(parseInt(e.target.value));
    setInputValue(`${parseInt(e.target.value)}, ${saturation}, ${lightness}`);
  };

  const handleColorChange = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = colorWindowRef.current.getBoundingClientRect();

    const clientX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.includes("touch") ? e.touches[0].clientY : e.clientY;

    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const y = Math.max(0, Math.min(rect.height, clientY - rect.top));

    const saturation = (x / rect.width) * 100;
    const normLightness = 100 - (y / rect.height) * 100;
    const lightness = (1 - y / rect.height) * (1 - saturation / 100 / 2) * 100;

    setSaturation(saturation.toFixed(2));
    setNormLightness(normLightness.toFixed(2));
    setLightness(lightness.toFixed(2));
    setInputValue(`${hue}, ${saturation.toFixed(0)}, ${lightness.toFixed(0)}`);
  };

  const handleSelect = () => {
    onSelectColor(color);
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleInputChange = (value) => {
    setInputValue(value);
  };

  const applyInputColor = () => {
    try {
      const array = inputValue.split(",");
      if (array.length !== 3) return;

      const newHue = parseInt(array[0].trim());
      const newSaturation = parseFloat(array[1].trim());
      const newLightness = parseFloat(array[2].trim());

      if (
        isNaN(newHue) ||
        isNaN(newSaturation) ||
        isNaN(newLightness) ||
        newHue < 0 ||
        newHue > 360 ||
        newSaturation < 0 ||
        newSaturation > 100 ||
        newLightness < 0 ||
        newLightness > 100
      )
        return;

      setHue(newHue);
      setSaturation(newSaturation.toFixed(2));
      setLightness(newLightness.toFixed(2));

      // Estimate normLightness by reversing your formula
      const box = colorWindowRef.current?.getBoundingClientRect();
      const yRatio = 1 - newLightness / 100 / (1 - newSaturation / 100 / 2);
      const normLight = 100 - yRatio * 100;
      setNormLightness(Math.max(0, Math.min(100, normLight)).toFixed(2));
    } catch (error) {
      console.error("Error parsing color values:", error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      applyInputColor();
      e.target.blur();
    }
  };

  const handlePopupClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="color-picker-overlay">
      <div className="dimming-cover color-picker" onClick={handleCancel}></div>
      <div
        className="color-picker-popup"
        ref={popupRef}
        onClick={handlePopupClick}
      >
        <div
          className="color-window"
          style={{ background: `hsl(${hue}, 100%, 50%)` }}
          onMouseDown={handleColorChange}
          onMouseMove={(e) => e.buttons === 1 && handleColorChange(e)}
          onTouchStart={handleColorChange}
          onTouchMove={handleColorChange}
          ref={colorWindowRef}
        >
          <div className="color-window-overlay"></div>
          <div
            className="color-selector"
            style={{
              left: `${saturation}%`,
              top: `${100 - normLightness}%`,
              backgroundColor: color,
            }}
          ></div>
        </div>

        <input
          type="range"
          min="0"
          max="360"
          value={hue}
          onChange={handleHueChange}
          className="hue-slider"
        />

        <div className="color-preview" style={{ backgroundColor: color }}></div>

        <InputField
          label={"HSL:"}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={applyInputColor}
          onKeyDown={handleKeyDown}
          placeholder="hue, saturation, lightness"
        />

        <div className="action-buttons">
          <Button
            onClick={handleSelect}
            btnStyle="btn-positive"
            icon={"apply"}
            className="mb-2"
          >
            {t("Select")}
          </Button>
          <Button
            onClick={handleCancel}
            btnStyle="btn-negative"
            icon={"cancel"}
          >
            {t("Cancel")}
          </Button>
        </div>
      </div>
    </div>
  );
}
